import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import divyaData from '../../data/divyaprabandham.json';
import { ChevronRight, BookOpen } from 'lucide-react';

const T = { bg:'#f0f5e8', cream:'#fffef0', greenDk:'#1b5e20', maroon:'#6d1b1b', orange:'#e65100', border:'#c8e6c9', header:'linear-gradient(135deg,#090b72,#1a237e)' };

/* Horizontally scrollable quick-nav bubbles */
const QuickNav = ({ currentSlug }) => {
  const scrollRef = useRef(null);

  useEffect(() => {
    // Auto-scroll to the active bubble
    if (scrollRef.current) {
      const active = scrollRef.current.querySelector('[data-active="true"]');
      if (active) {
        active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [currentSlug]);

  return (
    <div
      ref={scrollRef}
      style={{
        display: 'flex',
        gap: '6px',
        overflowX: 'auto',
        padding: '8px 4px',
        marginBottom: '10px',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      <style>{`.quick-nav-wrap::-webkit-scrollbar { display: none; }`}</style>
      {divyaData.map(item => {
        const isActive = item.slug === currentSlug;
        return (
          <Link
            key={item.slug}
            to={`/anmikam/divyaprabandham/${item.slug}`}
            data-active={isActive ? 'true' : 'false'}
            style={{
              flexShrink: 0,
              padding: '4px 10px',
              borderRadius: '14px',
              fontSize: '0.65rem',
              fontWeight: isActive ? 700 : 500,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              background: isActive ? T.greenDk : '#fff',
              color: isActive ? '#fff' : T.maroon,
              border: `1px solid ${isActive ? T.greenDk : T.border}`,
              boxShadow: isActive ? '0 2px 4px rgba(0,0,0,0.15)' : '0 1px 2px rgba(0,0,0,0.06)',
              transition: 'all 0.2s ease',
            }}
          >
            {item.title}
          </Link>
        );
      })}
    </div>
  );
};

const DivyaprabandhamPage = () => {
  const { slug } = useParams();
  const [content, setContent] = useState(null);

  useEffect(() => {
    if (slug) {
      const found = divyaData.find(d => d.slug === slug);
      setContent(found || null);
    } else {
      setContent(null);
    }
  }, [slug]);

  useEffect(() => {
    if (content) {
      document.title = `${content.title} - Divyaprabandham | Ahobilam`;
      let canonical = document.querySelector("link[rel='canonical']");
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", `https://ahobilam.com/anmikam/divyaprabandham/${content.slug}`);

      let metaDesc = document.querySelector("meta[name='description']");
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.setAttribute("name", "description");
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute("content", `${content.title} - Nalayira Divya Prabandham pasurams, Tamil text and meaning.`);
    } else {
      document.title = "Nalayira Divyaprabandham - திவ்ய பிரபந்தம் | Ahobilam";
      let canonical = document.querySelector("link[rel='canonical']");
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", "https://ahobilam.com/anmikam/divyaprabandham");
    }
  }, [content, slug]);

  if (!slug || !content) {
    return (
      <div style={{background:T.bg, minHeight:'80vh', padding:'15px', fontFamily:'sans-serif'}}>
        <div style={{background:T.header, padding:'15px', borderRadius:'10px', textAlign:'center', marginBottom:'10px', boxShadow:'0 4px 6px rgba(0,0,0,0.1)'}}>
          <h1 style={{color:'#fff', margin:0, fontSize:'1.2rem', fontWeight:900}}>📖 Divyaprabandham</h1>
          <div style={{color:'#c8e6c9', fontSize:'0.8rem'}}>Nalayira Divya Prabandham</div>
        </div>

        {/* Scrollable bubbles on index page too */}
        <QuickNav currentSlug="" />
        
        <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
          {divyaData.map(item => (
            <Link 
              key={item.slug} 
              to={`/anmikam/divyaprabandham/${item.slug}`}
              style={{
                textDecoration:'none', 
                background:T.cream, 
                border:`1px solid ${T.border}`,
                padding:'15px',
                borderRadius:'8px',
                color:T.maroon,
                fontWeight:'bold',
                display:'flex',
                justifyContent:'space-between',
                alignItems:'center',
                boxShadow:'0 2px 4px rgba(0,0,0,0.05)'
              }}
            >
              <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                <BookOpen size={18} color={T.orange} />
                {item.title}
              </div>
              <ChevronRight size={18} color={T.greenDk} />
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{background:T.bg, minHeight:'80vh', padding:'15px', fontFamily:'sans-serif'}}>
      <div style={{background:T.header, padding:'15px', borderRadius:'10px', textAlign:'center', marginBottom:'4px', boxShadow:'0 4px 6px rgba(0,0,0,0.1)'}}>
        <h1 style={{color:'#fff', margin:0, fontSize:'1.2rem', fontWeight:900}}>{content.title}</h1>
        <div style={{color:'#c8e6c9', fontSize:'0.8rem'}}>Divyaprabandham</div>
      </div>

      {/* Scrollable quick-nav bubbles right under header */}
      <QuickNav currentSlug={slug} />
      
      <div 
        style={{
          background:T.cream, 
          padding:'20px', 
          borderRadius:'10px', 
          border:`1px solid ${T.border}`,
          color:'#333',
          lineHeight:'1.6',
          boxShadow:'0 4px 6px rgba(0,0,0,0.05)'
        }}
        dangerouslySetInnerHTML={{ __html: content.contentHtml }}
      />
      
      <div style={{marginTop:'20px', textAlign:'center'}}>
        <Link 
          to="/anmikam/divyaprabandham" 
          style={{
            display:'inline-block',
            padding:'10px 20px',
            background:T.greenDk,
            color:'#fff',
            textDecoration:'none',
            borderRadius:'20px',
            fontSize:'0.9rem',
            fontWeight:'bold'
          }}
        >
          &larr; Back to Index
        </Link>
      </div>
    </div>
  );
};

export default DivyaprabandhamPage;
